import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from "@angular/forms";
import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { ErrorMessage } from "src/utils/error-message";
import { AbstractControl } from "@angular/forms";
import { LabelMode } from "src/utils/label-mode.enum";
import { GenerateRandom } from "src/utils/random";

@Component({
  selector: "app-input",
  templateUrl: "./input.component.html",
  styleUrls: ["./input.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class InputComponent implements OnInit, OnDestroy, ControlValueAccessor {
  subscription: Subscription;

  @Input()
  set isDisabled(value: boolean) {
    if (value) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }
  @Input()
  set isReadyOnly(value: boolean) {
    this.readonly = value;
  }

  @Input()
  set error(value: any) {
    this.isError = false;
    this.errorMessage = "";

    if (value) {
      this.isError = true;
      this.errorMessage = new ErrorMessage().getErrorMessage(value);
    }
  }

  @Input()
  set valid(value: any) {
    this.isValid = false;
    if (value) {
      this.isValid = true;
    }
  }

  @Input()
  set value(value: any) {
    if (value) {
      this.writeValue(value);
    }
  }

  @Input() set isCurrency(value: boolean) {
    if (value === true) {
      this.mask = "separator.2";
      this.thousandSeparator = ",";
      this.dropSpecialCharacters = true;
      this.isFormatCurrency = true;
    }
  }

  @Input() name = GenerateRandom(6);
  @Input() type = "text";
  @Input() autocomplete = "off";
  @Input() label: string;
  @Input() addonLabel: string = "";
  @Input() labelValue: string = "";
  @Input() placeholder = "";
  @Input() mask: string;
  @Input() dropSpecialCharacters: boolean = false;
  @Input() allowNegativeNumbers: boolean = false;
  @Input() thousandSeparator: string;
  @Input() specialCharacters: string[] = [];
  @Input() minlength: number = 0;
  @Input() maxlength: number = 1000;
  @Input() tabindex: number = 0;
  @Input() maskPrefix: string = "";
  @Input() maskSuffix: string = "";
  @Input() suffix: string;
  @Input() inputClass: string;
  @Input() classes: string;
  @Input() errorMessage: string = "VALIDATOR.REQUIRED";
  @Input() showErrorMessage: boolean = true;

  /**
   * Opt-in client-side validation.
   *
   * `[error]` carries what the SERVER said, which arrives only after a submit.
   * Bind the control here as well and the field also says what is wrong while
   * it is being filled in — the sign-up form accepted "not-an-email" and a
   * one-character password in silence, then failed on submit with a message
   * nowhere near the field that caused it.
   *
   * Opt-in on purpose: every form in the app shares this component, and turning
   * live validation on everywhere at once would change screens nobody asked me
   * to touch.
   */
  @Input() control?: AbstractControl | null;

  /** The message for the control's first failing rule, or "" when it is fine. */
  get controlError(): string {
    const c = this.control;
    // `touched` only, deliberately.
    //
    // `dirty` looks like the friendlier test and is useless here: the sign-up
    // form arrives with every control already dirty — something patches the
    // group as it is built — so `dirty || touched` painted eight fields red and
    // six error messages the instant the dialog opened, before anyone typed a
    // character. `touched` is the honest signal: false on a fresh form, true the
    // moment a field has been visited and left.
    if (!c || !c.errors || !c.touched) return "";
    const e = c.errors;
    if (e["required"]) return "VALIDATOR.REQUIRED";
    if (e["email"] || e["pattern"]) return "VALIDATOR.INVALID_EMAIL";
    if (e["minlength"]) return "VALIDATOR.PASSWORD_INVALID";
    if (e["ConfirmPassword"] || e["confirmPassword"]) return "VALIDATOR.PASSWORD_MISMATCH";
    if (e["mask"]) return "VALIDATOR.REQUIRED";
    return "VALIDATOR.REQUIRED";
  }

  /** Red border for either source of trouble. */
  get invalidState(): boolean {
    return this.isError || !!this.controlError;
  }

  /** Server message first — it is the more specific of the two. */
  get shownError(): string {
    return this.isError ? this.errorMessage : this.controlError;
  }
  @Input() minVal = null;
  @Input() maxVal = null;
  @Input() required: boolean = false;

  @Input() debouceTime: number = 0;

  @Input() set labelMode(value: LabelMode) {
    this.mode = value;
  }

  @Output() valueChange = new EventEmitter();
  @Output() inputChange = new EventEmitter();

  LabelMode = LabelMode;
  mode: LabelMode = LabelMode.DEFAULT;

  form = new FormControl();

  isError = false;
  isValid = false;

  showPwd = false;
  readonly = false;

  isFormatCurrency = false;

  onChange = (value: string) => {};
  onTouched = (value: string) => {};

  /**
   * Report that the field has been visited.
   *
   * This accessor never called `onTouched`, so `touched` stayed false for the
   * life of the form — which is why validation could not key off it and why
   * `dirty` (already true on this form before anyone types) was the only flag
   * left. A ControlValueAccessor that never reports touched is incomplete;
   * wiring blur fixes the flag for every consumer, not just the messages.
   */
  markTouched(): void {
    this.onTouched("");
    this.control?.markAsTouched();
  }

  constructor() {}

  ngOnInit(): void {
    this.subscription = this.form.valueChanges
      .pipe(debounceTime(this.debouceTime), distinctUntilChanged())
      .subscribe((val) => {
        let value = this.form.value;
        // if (this.isFormatCurrency) {
        //   value = this.formatCurrency(value);
        // }
        if (typeof this.form.value === "string") {
          value = this.form.value.trim();
        }
        this.valueChange.emit(value);
        this.onChange(value);
      });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
  formatCurrency(value: any) {
    if (value.toString().includes(".")) {
      value = value.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    } else {
      value = value.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    }
    return value;
  }

  writeValue(obj: any): void {
    // Use patchValue fot unmatch key
    this.form.setValue(obj);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  onValueChange(event: string) {}

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onInputChange(value?: any) {
    const inputElement = value.target as HTMLInputElement;
    let inputValue: string | number = inputElement.value;
    if (this.isFormatCurrency) {
      inputValue = +inputValue.toString().replace(/,/g, "");
    }
    this.inputChange.emit(inputValue);
  }

  setDisabledState?(isDisabled: boolean): void {}
}
